import * as Api from '@ViewModels';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LevStandardQueryMeta } from '../../web/ReactQueryProvider';
import { useUserSession } from '../../models/hooks/appStateHooks';

const SHARED_MAILBOXES_PATH = 'user/preferences/sharedMailboxes';

const sharedMailboxesQueryKey = (impersonationContext?: Api.IImpersonationContext) =>
	['sharedMailboxes', impersonationContext] as const;

const UNAVAILABLE_COLLECTION: Api.ISharedMailboxCollection = {
	available: false,
	mailboxes: [],
	maxMailboxes: 0,
	reconnectRequired: false,
	verificationRequired: false,
	testInFlightId: null,
};

/**
 * Polls while a test round-trip is outstanding so every observer (including the send-from picker's gate)
 * sees `verified` flip. One poll for the whole collection rather than one per mailbox.
 */
export function useSharedMailboxesQuery({
	impersonationContext,
	enabled = true,
	refetchOnWindowFocus = false,
	pollWhileTesting = false,
	pollIntervalMs = 3000,
	meta,
}: {
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
	refetchOnWindowFocus?: boolean;
	pollWhileTesting?: boolean;
	pollIntervalMs?: number;
	meta?: LevStandardQueryMeta;
} = {}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		refetchOnWindowFocus,
		queryFn: async () => {
			// A 404 means the API predates the collection routes; report unavailable so callers hide the feature.
			const result = await userSession.webServiceHelper.callAsync<Api.ISharedMailboxCollection>({
				path: Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath: SHARED_MAILBOXES_PATH }),
				method: 'GET',
				throwOn404: false,
			});
			return result ?? UNAVAILABLE_COLLECTION;
		},
		queryKey: sharedMailboxesQueryKey(impersonationContext),
		refetchInterval: query => (pollWhileTesting && query.state.data?.testInFlightId ? pollIntervalMs : false),
		// Users switch away (to their inbox, to check the test arrived) exactly while a test is validating;
		// without this the poll pauses in hidden tabs and the test falsely times out.
		refetchIntervalInBackground: true,
		meta,
	});
}

// On a 400/404/409 the backend returns a user-displayable `systemMessage`; surface it via `onError`.
interface IMutationOptions<TVariables> {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue, variables: TVariables) => void;
	onSuccess?: (data: Api.ISharedMailboxCollection, variables: TVariables) => void;
}

function useCollectionMutation<TVariables>(
	{ impersonationContext, onError, onSuccess }: IMutationOptions<TVariables>,
	request: (variables: TVariables) => { urlPath: string; method: 'POST' | 'PUT' | 'DELETE'; body?: unknown }
) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (variables: TVariables) => {
			const { urlPath, method, body } = request(variables);
			return userSession.webServiceHelper.callAsync<Api.ISharedMailboxCollection>(
				Api.ImpersonationBroker.composeApiUrl({ impersonationContext, urlPath }),
				method,
				body
			);
		},
		onError,
		onSuccess: (data, variables) => {
			queryClient.setQueryData(sharedMailboxesQueryKey(impersonationContext), data);
			onSuccess?.(data, variables);
		},
	});
}

export function useAddSharedMailboxMutation(options: IMutationOptions<{ emailAddress: string; name?: string }>) {
	return useCollectionMutation<{ emailAddress: string; name?: string }>(options, ({ emailAddress, name }) => ({
		urlPath: SHARED_MAILBOXES_PATH,
		method: 'POST',
		body: { emailAddress, name },
	}));
}

export function useUpdateSharedMailboxMutation(
	options: IMutationOptions<{ id: string; emailAddress: string; name?: string }>
) {
	return useCollectionMutation<{ id: string; emailAddress: string; name?: string }>(
		options,
		({ id, emailAddress, name }) => ({
			urlPath: `${SHARED_MAILBOXES_PATH}/${encodeURIComponent(id)}`,
			method: 'PUT',
			body: { emailAddress, name },
		})
	);
}

export function useRemoveSharedMailboxMutation(options: IMutationOptions<{ id: string }>) {
	return useCollectionMutation<{ id: string }>(options, ({ id }) => ({
		urlPath: `${SHARED_MAILBOXES_PATH}/${encodeURIComponent(id)}`,
		method: 'DELETE',
	}));
}

/**
 * Success only means the provider accepted the send. Verification is the round-trip, observed by polling the
 * collection. Returns 409 when another mailbox's test is still running.
 */
export function useTestSharedMailboxMutation({
	impersonationContext,
	onError,
	onSuccess,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (variables: { id: string }) => void;
}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ id }: { id: string }) => {
			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `${SHARED_MAILBOXES_PATH}/${encodeURIComponent(id)}/test`,
				}),
				'POST'
			);
		},
		onError,
		onSuccess: (_data, variables) => {
			// The endpoint returns void, so refetch to land `testInFlightId` in the cache; that is what starts polling.
			queryClient.invalidateQueries({ queryKey: sharedMailboxesQueryKey(impersonationContext) });
			onSuccess?.(variables);
		},
	});
}
